/**
 * AirKey Trace - Unified Cross-Platform Logging Service
 *
 * This service provides a unified logging interface for AirKey Web NFC application
 * that enables cross-platform tracing with iOS app clip events.
 *
 * All events are logged as 'AirKeyTrace' to New Relic with standardized attributes:
 * - policyProcessId: Unique session/process identifier
 * - intentOwner: Business entity identifier
 * - workflow: Workflow type
 * - client: Platform identifier ('web' or 'ios')
 * - view: Page/screen name where action occurred
 * - action: Action type (generic, reusable)
 *
 * This service runs in parallel with existing New Relic logging.
 * It does NOT replace existing logs - both systems will run simultaneously.
 *
 * @see docs/UNIFIED_LOGGING.md for implementation guide
 * @see src/types/AirKeyTraceConstants.ts for View and Action enums
 */

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AirKeyAction, AirKeyView } from '@/types/AirKeyTraceConstants';

/**
 * Parameters for AirKeyTrace event
 */
export interface AirKeyTraceParams {
  /** Policy Process ID — unique session/process identifier for all routes */
  policyProcessId?: string;
  /** Business entity identifier (e.g., 'acs', 'ease') */
  intentOwner: string;
  /** Workflow type (e.g., '3ds', 'av') */
  workflow: string;
  /** Platform identifier */
  client: 'web' | 'ios';
  /** Page/screen name where action occurred */
  view: AirKeyView;
  /** Action taken (generic, reusable action type) */
  action: AirKeyAction;
  /** (Optional) Additional contextual data */
  // TODO: If we know the format of some specific data, we should standardize it in the typing for additional data
  additionalData?: Record<string, unknown>;
}

/**
 * Send unified AirKeyTrace event to New Relic
 *
 * This function sends events using recordCustomEvent to ensure web and iOS
 * logs are stored in the same event type (AirKeyTrace) for unified querying.
 * This avoids having to join PageAction and custom event tables.
 *
 * @example
 * ```typescript
 * sendAirKeyTrace({
 *   policyProcessId: 'session-123',
 *   intentOwner: 'ease',
 *   workflow: 'av',
 *   client: 'web',
 *   view: 'intro',
 *   action: 'confirm',
 * });
 * ```
 */
export function sendAirKeyTrace(params: AirKeyTraceParams): void {
  const {
    policyProcessId,
    intentOwner,
    workflow,
    client,
    view,
    action,
    additionalData = {},
  } = params;

  const event = {
    ...(policyProcessId && { policyProcessId }),
    intentOwner,
    workflow,
    client,
    view,
    action,
    ...additionalData,
  };

  // Use recordCustomEvent to send AirKeyTrace event to custom event table
  // This ensures web and iOS logs are in the same event type for unified querying
  window.newrelic?.recordCustomEvent('AirKeyTrace', event);
}

/**
 * Hook to automatically extract URL parameters and return pre-configured sendAirKeyTrace
 *
 * This hook extracts policyProcessId, intentOwner, and workflow from URL params,
 * then returns a convenience function that auto-populates these values.
 *
 * For legacy routes (e.g., /3ds/:policyProcessId), this hook automatically provides
 * default values for intentOwner and workflow since they're not in the URL.
 *
 * @returns Function to send AirKeyTrace with auto-populated URL params
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const sendTrace = useAirKeyTrace();
 *
 *   const handleClick = () => {
 *     sendTrace({
 *       view: 'intro',
 *       action: 'confirm',
 *     });
 *   };
 * }
 * ```
 */
export function useAirKeyTrace() {
  const { policyProcessId, intentOwner, workflow } = useParams();

  // Detect legacy 3DS route: has policyProcessId but no intentOwner/workflow in URL
  const isLegacyRoute = !!policyProcessId && !intentOwner;

  const defaultIntentOwner = isLegacyRoute ? 'acs' : '';
  const defaultWorkflow = isLegacyRoute ? '3ds' : '';

  return (
    params: Omit<
      AirKeyTraceParams,
      'policyProcessId' | 'intentOwner' | 'workflow' | 'client'
    > & {
      policyProcessId?: string;
      intentOwner?: string;
      workflow?: string;
    },
  ) => {
    sendAirKeyTrace({
      ...params,
      ...(policyProcessId && {
        policyProcessId: params.policyProcessId ?? policyProcessId,
      }),
      intentOwner: params.intentOwner ?? intentOwner ?? defaultIntentOwner,
      workflow: params.workflow ?? workflow ?? defaultWorkflow,
      client: 'web',
    });
  };
}

/**
 * Hook to automatically log page view on component mount
 * Automatically sends AirKeyTrace event with VIEW_LOADED action when component mounts.
 *
 * @param view - The view/page name (use AirKeyView enum)
 *
 * @example
 * ```typescript
 * function AuthIntro() {
 *   useAirKeyTracePageView('intro');
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAirKeyTracePageView(
  view: AirKeyView,
  additionalData?: Record<string, unknown>,
) {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view,
      action: 'view_loaded',
      ...(additionalData && { additionalData }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
